<div class="dl-wrapper">
	<!-- =============================== LISTE =============================== -->
	@if (view() === 'list') {
		<header class="dl-header">
			<div class="dl-header-left">
				<i class="bi bi-easel"></i>
				<h1>Druck-Layout</h1>
				@if (projectName()) {
					<span class="dl-project">· {{ projectName() }}</span>
				}
			</div>
			<div class="dl-header-right">
				<button type="button" class="btn btn-default" (click)="goBack()" title="Zurück">
					<i class="bi bi-arrow-left"></i> <span class="d-none d-sm-inline">Zurück</span>
				</button>
			</div>
		</header>

		<div class="dl-list-body">
			@if (!projectId()) {
				<div class="dl-empty">
					<i class="bi bi-folder2-open"></i>
					<p>Kein Projekt geöffnet.</p>
					<p class="dl-empty-hint">Öffne zuerst ein Projekt — Druck-Layouts gehören jeweils zu einem Projekt.</p>
				</div>
			} @else {
				<div class="dl-list-toolbar">
					<button type="button" class="btn btn-primary" (click)="newLayout()">
						<i class="bi bi-plus-lg"></i> Neues Layout
					</button>
				</div>

				@if (loading()) {
					<div class="dl-loading"><span class="spinner-border spinner-border-sm"></span> Lädt…</div>
				} @else if (summaries().length === 0) {
					<div class="dl-empty">
						<i class="bi bi-easel"></i>
						<p>Noch keine Druck-Layouts.</p>
						<p class="dl-empty-hint">Lege mit „Neues Layout" dein erstes druckfertiges Kartenlayout an.</p>
					</div>
				} @else {
					<div class="dl-cards">
						@for (s of summaries(); track s.layoutId) {
							<div class="dl-card" (click)="open(s.layoutId)" tabindex="0"
								(keydown.enter)="open(s.layoutId)" role="button">
								<div class="dl-card-thumb" [class.landscape]="s.page.orientation === 'landscape'">
									<i class="bi bi-easel"></i>
								</div>
								<div class="dl-card-body">
									<div class="dl-card-name" [title]="s.name">{{ s.name }}</div>
									<div class="dl-card-meta">
										{{ s.page.orientation === 'portrait' ? 'Hochformat' : 'Querformat' }}
										· {{ s.elementCount }} Element{{ s.elementCount === 1 ? '' : 'e' }}
									</div>
									@if (s.updatedAt) {
										<div class="dl-card-date">Geändert: {{ formatDate(s.updatedAt) }}</div>
									}
								</div>
								<div class="dl-card-actions">
									<button type="button" class="btn btn-sm btn-outline-secondary"
										(click)="rename(s, $event)" title="Umbenennen">
										<i class="bi bi-pencil"></i>
									</button>
									<button type="button" class="btn btn-sm btn-outline-danger"
										(click)="remove(s, $event)" title="Löschen">
										<i class="bi bi-trash"></i>
									</button>
								</div>
							</div>
						}
					</div>
				}
			}
		</div>
	}

	<!-- =============================== EDITOR =============================== -->
	@if (view() === 'editor') {
		<header class="dl-header">
			<div class="dl-header-left">
				<button type="button" class="btn btn-default" (click)="backToList()" title="Zurück zur Liste">
					<i class="bi bi-arrow-left"></i> <span class="d-none d-sm-inline">Liste</span>
				</button>
				<h1 class="dl-editor-title" [title]="current()?.name">{{ current()?.name }}</h1>
				@if (dirty()) {
					<span class="dl-dirty" title="Ungespeicherte Änderungen">•</span>
				}
			</div>
			<div class="dl-header-right">
				<button type="button" class="btn btn-outline-primary" (click)="exportPdf()"
					[disabled]="exporting() || editorElements().length === 0" title="Als PDF (A4) exportieren">
					@if (exporting()) {
						<span class="spinner-border spinner-border-sm"></span>
					} @else {
						<i class="bi bi-filetype-pdf"></i>
					}
					<span class="d-none d-sm-inline"> PDF</span>
				</button>
				<button type="button" class="btn btn-success" (click)="save()" [disabled]="saving()" title="Speichern">
					@if (saving()) {
						<span class="spinner-border spinner-border-sm"></span>
					} @else {
						<i class="bi bi-download"></i>
					}
					<span class="d-none d-sm-inline"> Speichern</span>
				</button>
			</div>
		</header>

		<div class="dl-body">
			<!-- Werkzeugleiste: fügt Elemente ein -->
			<aside class="dl-toolbar">
				@for (t of tools; track t.id) {
					<button type="button" class="dl-tool" [title]="t.label + ' einfügen'" (click)="canvas.addElement(t.id)">
						<i class="bi" [ngClass]="'bi-' + t.icon"></i>
						<span>{{ t.label }}</span>
					</button>
				}
			</aside>

			<!-- Arbeitsflaeche -->
			<main class="dl-workspace">
				<!-- Klick neben die Seite (Arbeitsfläche) hebt die Auswahl auf.
				     Element-/Griff-Klicks stoppen die Propagation, bleiben also ausgewählt. -->
				<div class="dl-canvas-scroll" (pointerdown)="canvas.select(null)">
					<wd-layout-canvas
						#canvas
						[elements]="editorElements()"
						(elementsChange)="onCanvasElements($event)"
						(selectionChange)="selectedId.set($event)"
						[pageWidthMm]="pageWidthMm()"
						[pageHeightMm]="pageHeightMm()"
						[zoomPct]="zoomPct()"
						[snap]="snapOn()"
					></wd-layout-canvas>
				</div>

				<footer class="dl-statusbar">
					<div class="btn-group" role="group" aria-label="Ausrichtung">
						<button type="button" class="btn btn-sm"
							[class.btn-primary]="orientation() === 'portrait'"
							[class.btn-outline-secondary]="orientation() !== 'portrait'"
							(click)="setOrientation('portrait')">
							<i class="bi bi-file-earmark"></i> Hoch
						</button>
						<button type="button" class="btn btn-sm"
							[class.btn-primary]="orientation() === 'landscape'"
							[class.btn-outline-secondary]="orientation() !== 'landscape'"
							(click)="setOrientation('landscape')">
							<i class="bi bi-file-earmark-break"></i> Quer
						</button>
					</div>

					<label class="dl-snap-toggle" title="An Raster, Kanten und Rändern einrasten">
						<input type="checkbox" [checked]="snapOn()" (change)="snapOn.set($any($event.target).checked)" />
						<i class="bi bi-magnet"></i> <span class="d-none d-sm-inline">Magnetisch</span>
					</label>

					<div class="dl-zoom">
						<button type="button" class="btn btn-sm btn-outline-secondary" (click)="zoomOut()" title="Verkleinern">
							<i class="bi bi-dash-lg"></i>
						</button>
						<button type="button" class="btn btn-sm btn-outline-secondary" (click)="resetZoom()" title="Auf 100 % zurücksetzen">
							{{ zoomPct() }} %
						</button>
						<button type="button" class="btn btn-sm btn-outline-secondary" (click)="zoomIn()" title="Vergrößern">
							<i class="bi bi-plus-lg"></i>
						</button>
					</div>
				</footer>
			</main>

			<!-- Rechtes Panel: Elemente-Liste (immer) + Eigenschaften (bei Auswahl) -->
			<aside class="dl-side">
				<div class="dl-side-section dl-elements-section">
					<div class="dl-side-head"><i class="bi bi-stack"></i> Elemente</div>
					<div class="dl-elements">
						@for (el of elementsByZ(); track el.id) {
							<button type="button" class="dl-el-row" [class.active]="el.id === selectedId()"
								(click)="canvas.select(el.id)" [title]="labelForType(el.type)">
								<i class="bi" [ngClass]="'bi-' + iconForType(el.type)"></i>
								<span class="dl-el-row-label">{{ labelForType(el.type) }}</span>
							</button>
						} @empty {
							<div class="dl-elements-empty">Noch keine Elemente. Über die Werkzeugleiste links einfügen.</div>
						}
					</div>
				</div>

				@if (selectedElement(); as sel) {
					<div class="dl-side-section dl-props">
						<div class="dl-side-head">
							<i class="bi" [ngClass]="'bi-' + iconForType(sel.type)"></i> {{ labelForType(sel.type) }}
						</div>

						<div class="dl-props-grid">
							<label>X <input type="number" step="1" [value]="sel.xMm" (change)="setProp(canvas, 'xMm', $event)" /></label>
							<label>Y <input type="number" step="1" [value]="sel.yMm" (change)="setProp(canvas, 'yMm', $event)" /></label>
							<label>Breite <input type="number" step="1" min="8" [value]="sel.wMm" (change)="setProp(canvas, 'wMm', $event)" /></label>
							<label>Höhe <input type="number" step="1" min="8" [value]="sel.hMm" (change)="setProp(canvas, 'hMm', $event)" /></label>
							<label>Drehung <input type="number" step="5" [value]="sel.rotationDeg || 0" (change)="setProp(canvas, 'rotationDeg', $event)" /></label>
						</div>

						<!-- Typ-spezifische Eigenschaften -->
						@switch (sel.type) {
							@case ('map') {
								<div class="dl-prop-type">
									<button type="button" class="btn btn-sm btn-primary" (click)="captureMap(canvas, sel)">
										<i class="bi bi-camera"></i> {{ strProp(sel, 'src', '') ? 'Karte neu aufnehmen' : 'Karte aufnehmen' }}
									</button>
									<button type="button" class="btn btn-sm btn-outline-primary" (click)="captureMapFromCurrentView(canvas, sel)"
										title="Aktuelle Ansicht der Hauptkarte direkt aufnehmen">
										<i class="bi bi-geo-alt"></i> Aktuelle Karte übernehmen
									</button>
									@if (mapScale(sel) > 0) {
										<div class="dl-map-scale">Druckmaßstab 1:{{ mapScale(sel) }}</div>
									}
									<div class="dl-hint">„Karte aufnehmen" öffnet die Vorschau zum Rahmen. „Aktuelle Karte übernehmen" nimmt sofort den Ausschnitt der Hauptkarte. Der Maßstab folgt der Element-Größe.</div>
								</div>
							}
							@case ('text') {
								<div class="dl-prop-type">
									<textarea class="form-control form-control-sm" rows="3" placeholder="Text eingeben…"
										[value]="strProp(sel, 'text', '')" (change)="setProps(canvas, sel, 'text', $any($event.target).value)"></textarea>
									<label>Größe (pt)
										<input type="number" min="4" step="1" [value]="numProp(sel, 'fontPt', 11)"
											(change)="setProps(canvas, sel, 'fontPt', +$any($event.target).value)" /></label>
									<label>Farbe
										<input type="color" [value]="strProp(sel, 'color', '#000000')"
											(input)="setProps(canvas, sel, 'color', $any($event.target).value)" /></label>
									<div class="dl-align btn-group" role="group" aria-label="Ausrichtung">
										<button type="button" class="btn btn-sm" [class.btn-primary]="strProp(sel,'align','left')==='left'"
											[class.btn-outline-secondary]="strProp(sel,'align','left')!=='left'" (click)="setProps(canvas, sel, 'align', 'left')" title="Linksbündig"><i class="bi bi-text-left"></i></button>
										<button type="button" class="btn btn-sm" [class.btn-primary]="strProp(sel,'align','left')==='center'"
											[class.btn-outline-secondary]="strProp(sel,'align','left')!=='center'" (click)="setProps(canvas, sel, 'align', 'center')" title="Zentriert"><i class="bi bi-text-center"></i></button>
										<button type="button" class="btn btn-sm" [class.btn-primary]="strProp(sel,'align','left')==='right'"
											[class.btn-outline-secondary]="strProp(sel,'align','left')!=='right'" (click)="setProps(canvas, sel, 'align', 'right')" title="Rechtsbündig"><i class="bi bi-text-right"></i></button>
									</div>
									<label class="dl-check"><input type="checkbox" [checked]="boolProp(sel, 'bold')"
										(change)="setProps(canvas, sel, 'bold', $any($event.target).checked)" /> Fett</label>
								</div>
							}
							@case ('image') {
								<div class="dl-prop-type">
									<input type="file" class="form-control form-control-sm" accept="image/*" (change)="onImagePick(canvas, sel, $event)" />
									<div class="btn-group" role="group" aria-label="Einpassung">
										<button type="button" class="btn btn-sm" [class.btn-primary]="strProp(sel,'fit','contain')==='contain'"
											[class.btn-outline-secondary]="strProp(sel,'fit','contain')!=='contain'" (click)="setProps(canvas, sel, 'fit', 'contain')">Einpassen</button>
										<button type="button" class="btn btn-sm" [class.btn-primary]="strProp(sel,'fit','contain')==='cover'"
											[class.btn-outline-secondary]="strProp(sel,'fit','contain')!=='cover'" (click)="setProps(canvas, sel, 'fit', 'cover')">Füllen</button>
									</div>
								</div>
							}
							@case ('scale') {
								<div class="dl-prop-type">
									@if (hasMapSource()) {
										<div class="dl-map-scale">Maßstab 1:{{ currentPrintScale() }}</div>
										<div class="dl-hint">Folgt automatisch der Karte.</div>
									} @else {
										<div class="dl-hint">Erst eine Karte aufnehmen — der Maßstab folgt ihr dann automatisch.</div>
									}
									<label>Segmente
										<input type="number" min="1" max="8" step="1" [value]="numProp(sel, 'segments', 1)"
											(change)="setProps(canvas, sel, 'segments', +$any($event.target).value)" /></label>
									<div class="btn-group" role="group" aria-label="Darstellung">
										<button type="button" class="btn btn-sm" [class.btn-primary]="strProp(sel,'show','both')==='both'"
											[class.btn-outline-secondary]="strProp(sel,'show','both')!=='both'" (click)="setScaleShow(canvas, sel, 'both')">Balken + Text</button>
										<button type="button" class="btn btn-sm" [class.btn-primary]="strProp(sel,'show','both')==='bar'"
											[class.btn-outline-secondary]="strProp(sel,'show','both')!=='bar'" (click)="setScaleShow(canvas, sel, 'bar')">Balken</button>
										<button type="button" class="btn btn-sm" [class.btn-primary]="strProp(sel,'show','both')==='text'"
											[class.btn-outline-secondary]="strProp(sel,'show','both')!=='text'" (click)="setScaleShow(canvas, sel, 'text')">Text</button>
									</div>
									<div class="btn-group" role="group" aria-label="Ausrichtung">
										<button type="button" class="btn btn-sm" [class.btn-primary]="strProp(sel,'align','right')==='left'"
											[class.btn-outline-secondary]="strProp(sel,'align','right')!=='left'" (click)="setProps(canvas, sel, 'align', 'left')">Links</button>
										<button type="button" class="btn btn-sm" [class.btn-primary]="strProp(sel,'align','right')==='right'"
											[class.btn-outline-secondary]="strProp(sel,'align','right')!=='right'" (click)="setProps(canvas, sel, 'align', 'right')">Rechts</button>
									</div>
								</div>
							}
							@case ('legend') {
								<div class="dl-prop-type">
									<button type="button" class="btn btn-sm btn-primary" (click)="refreshLegend(canvas, sel)" [disabled]="!hasMapSource()">
										<i class="bi bi-arrow-repeat"></i> Legende aus Karte aktualisieren
									</button>
									<div class="dl-hint">{{ legendRowCount(sel) }} Punktart(en) im Kartenausschnitt.</div>
									<label>Titel
										<input type="text" class="form-control form-control-sm" placeholder="z. B. Legende"
											[value]="strProp(sel, 'title', '')" (change)="setProps(canvas, sel, 'title', $any($event.target).value)" /></label>
									<label>Schrift (pt)
										<input type="number" min="4" step="1" [value]="numProp(sel, 'fontPt', 8)"
											(change)="setProps(canvas, sel, 'fontPt', +$any($event.target).value)" /></label>
									<label class="dl-check"><input type="checkbox" [checked]="boolProp(sel, 'frame')"
										(change)="setProps(canvas, sel, 'frame', $any($event.target).checked)" /> Heller Hintergrund (Lesbarkeit)</label>
									@if (!hasMapSource()) {
										<div class="dl-hint">Erst eine Karte aufnehmen — die Legende zeigt die dort sichtbaren Punktarten.</div>
									}
								</div>
							}
							@case ('north') {
								<div class="dl-prop-type">
									<label>Farbe
										<input type="color" [value]="strProp(sel, 'color', '#000000')"
											(input)="setProps(canvas, sel, 'color', $any($event.target).value)" /></label>
								</div>
							}
							@case ('draw') {
								<div class="dl-prop-type">
									<label>Form
										<select class="form-select form-select-sm" [value]="strProp(sel, 'shape', 'rect')"
											(change)="setProps(canvas, sel, 'shape', $any($event.target).value)">
											<option value="rect">Rechteck</option>
											<option value="ellipse">Ellipse</option>
											<option value="line">Linie</option>
											<option value="arrow">Pfeil</option>
										</select></label>
									<label>Linie
										<input type="color" [value]="strProp(sel, 'stroke', '#3c5a3c')"
											(input)="setProps(canvas, sel, 'stroke', $any($event.target).value)" /></label>
									<label>Dicke
										<input type="number" min="0.5" step="0.5" [value]="numProp(sel, 'strokeWidth', 2)"
											(change)="setProps(canvas, sel, 'strokeWidth', +$any($event.target).value)" /></label>
									<label class="dl-check"><input type="checkbox" [checked]="strProp(sel,'fill','none')!=='none'"
										(change)="setProps(canvas, sel, 'fill', $any($event.target).checked ? '#cfe0cf' : 'none')" /> Füllung</label>
									@if (strProp(sel, 'fill', 'none') !== 'none') {
										<label>Füllfarbe
											<input type="color" [value]="strProp(sel, 'fill', '#cfe0cf')"
												(input)="setProps(canvas, sel, 'fill', $any($event.target).value)" /></label>
									}
								</div>
							}
						}

						<div class="dl-props-actions">
							<button type="button" class="btn btn-sm btn-outline-secondary" (click)="canvas.bringToFront()" title="Nach vorne">
								<i class="bi bi-front"></i>
							</button>
							<button type="button" class="btn btn-sm btn-outline-secondary" (click)="canvas.sendToBack()" title="Nach hinten">
								<i class="bi bi-back"></i>
							</button>
							<button type="button" class="btn btn-sm btn-outline-secondary" (click)="canvas.duplicateSelected()" title="Duplizieren">
								<i class="bi bi-copy"></i>
							</button>
							<button type="button" class="btn btn-sm btn-outline-danger" (click)="canvas.removeSelected()" title="Löschen">
								<i class="bi bi-trash"></i>
							</button>
						</div>
					</div>
				}
			</aside>
		</div>
	}
</div>
